// Favourites + one-tap re-book server functions.
// Customers can favourite Helpers from browse/profile cards and re-book any past
// completed job in one tap. Re-booking duplicates the original job and sends a
// direct, single-recipient offer to the same Helper so they can accept-to-confirm.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const toggleFavorite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ helperId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    if (data.helperId === userId) throw new Error("You can't favourite yourself");

    const { data: existing } = await supabase
      .from("favorites")
      .select("id")
      .eq("user_id", userId)
      .eq("helper_id", data.helperId)
      .maybeSingle();

    if (existing) {
      const { error } = await supabase.from("favorites").delete().eq("id", existing.id);
      if (error) throw new Error(error.message);
      return { favorited: false };
    }

    const { error } = await supabase
      .from("favorites")
      .insert({ user_id: userId, helper_id: data.helperId });
    if (error) throw new Error(error.message);
    return { favorited: true };
  });

export type FavoriteHelper = {
  id: string;
  display_name: string;
  avatar_url: string | null;
  city: string | null;
  headline: string | null;
  hourly_rate: number | null;
  avg_rating: number;
  rating_count: number;
  jobs_completed: number;
  id_verified: boolean;
  background_check_passed: boolean;
  instant_book_enabled: boolean;
  favorited_at: string;
};

export const listMyFavorites = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ favorites: FavoriteHelper[] }> => {
    const { supabase, userId } = context;
    const { data: rows, error } = await supabase
      .from("favorites")
      .select("helper_id, created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(60);
    if (error) throw new Error(error.message);
    const ids = (rows ?? []).map((r) => r.helper_id);
    if (ids.length === 0) return { favorites: [] };

    const [{ data: helpers }, { data: profiles }] = await Promise.all([
      supabase
        .from("helper_profiles")
        .select(
          "id, headline, hourly_rate, avg_rating, rating_count, jobs_completed, id_verified, background_check_passed, instant_book_enabled, suspended",
        )
        .in("id", ids),
      supabase
        .from("profiles")
        .select("id, display_name, full_name, avatar_url, city")
        .in("id", ids),
    ]);
    const profileMap = new Map((profiles ?? []).map((p: any) => [p.id, p]));
    const createdAt = new Map((rows ?? []).map((r) => [r.helper_id, r.created_at]));

    const favorites: FavoriteHelper[] = (helpers ?? [])
      .filter((h: any) => !h.suspended)
      .map((h: any) => {
        const p: any = profileMap.get(h.id) ?? {};
        return {
          id: h.id,
          display_name: p.display_name || p.full_name || "Helper",
          avatar_url: p.avatar_url ?? null,
          city: p.city ?? null,
          headline: h.headline ?? null,
          hourly_rate: h.hourly_rate != null ? Number(h.hourly_rate) : null,
          avg_rating: Number(h.avg_rating ?? 0),
          rating_count: Number(h.rating_count ?? 0),
          jobs_completed: Number(h.jobs_completed ?? 0),
          id_verified: !!h.id_verified,
          background_check_passed: !!h.background_check_passed,
          instant_book_enabled: !!h.instant_book_enabled,
          favorited_at: createdAt.get(h.id) ?? new Date().toISOString(),
        };
      })
      // keep favourited order
      .sort((a, b) => (a.favorited_at < b.favorited_at ? 1 : -1));

    return { favorites };
  });

export const getMyFavoriteIds = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("favorites")
      .select("helper_id")
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ids: (data ?? []).map((r) => r.helper_id) };
  });

/**
 * Re-book: duplicate a previous completed booking's job, and send a single
 * direct offer to the original Helper. Customer is returned a new job_id and
 * sent to /dashboard — once the Helper accepts they'll be prompted to pay.
 */
export const rebookFromBooking = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        bookingId: z.string().uuid(),
        scheduled_at: z.string().datetime().optional().nullable(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // Pull the source booking + its job. RLS restricts to the customer.
    const { data: src, error: srcErr } = await supabase
      .from("bookings")
      .select(
        "id, customer_id, helper_id, status, job:jobs(id, title, description, postcode, category_id, budget_min, budget_max, urgency, photos)",
      )
      .eq("id", data.bookingId)
      .maybeSingle();
    if (srcErr || !src) throw new Error("Booking not found");
    if (src.customer_id !== userId) throw new Error("You can only re-book your own jobs");
    if (!src.job) throw new Error("Original job is unavailable");
    if (!["completed", "cancelled", "refunded"].includes(src.status)) {
      throw new Error("This booking is still active — finish it before re-booking");
    }

    // Confirm the original Helper is still active before re-targeting them.
    const { data: helper } = await supabase
      .from("helper_profiles")
      .select("id, available, suspended, instant_book_enabled")
      .eq("id", src.helper_id)
      .maybeSingle();
    const helperActive = !!helper && !helper.suspended;

    // Create the new job under the customer's own RLS context.
    const job: any = src.job;
    const { data: newJob, error: jobErr } = await supabase
      .from("jobs")
      .insert({
        customer_id: userId,
        category_id: job.category_id,
        title: job.title,
        description: job.description,
        raw_input: job.description,
        postcode: job.postcode,
        scheduled_at: data.scheduled_at ?? null,
        flexible_timing: !data.scheduled_at,
        urgency: job.urgency ?? "normal",
        budget_min: job.budget_min,
        budget_max: job.budget_max,
        photos: job.photos ?? [],
        status: "open",
      })
      .select("id, title")
      .single();
    if (jobErr || !newJob) throw new Error(jobErr?.message ?? "Could not create job");

    // Direct, single-recipient offer + notify the Helper. Admin client because
    // job_offers RLS scopes inserts to the helper themselves.
    if (helperActive) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await supabaseAdmin.from("job_offers").insert({
        job_id: newJob.id,
        helper_id: src.helper_id,
        score: 999, // prioritise above generic matches
      });
      await supabaseAdmin.from("notifications").insert({
        user_id: src.helper_id,
        type: "job_offer",
        title: "Repeat booking request",
        body: `A previous customer wants to re-book you for: ${newJob.title}`,
        link: "/helper/offers",
        data: { job_id: newJob.id, repeat_of_booking_id: data.bookingId },
      });
    }

    return {
      job_id: newJob.id,
      helper_id: src.helper_id,
      helper_active: helperActive,
    };
  });
